/**
 * Configuração central do i18n — i18next + react-i18next.
 *
 * - Idioma default: pt-BR
 * - Fallback: pt-BR (garante strings mesmo sem tradução nos outros idiomas)
 * - Detecção/persistência: localStorage ("priceshow_lang")
 * - Suporte inicial: pt-BR, en, es
 *
 * Importar este módulo UMA vez (em main.tsx), antes de montar o React.
 * Componentes usam `useTranslation()` de react-i18next.
 *
 * Para adicionar idioma: ver apps/admin/src/i18n/README.md.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import ptBR from "./locales/pt-BR.json";
import en from "./locales/en.json";
import es from "./locales/es.json";

/** Chave usada no localStorage para persistir a preferência do usuário. */
export const I18N_STORAGE_KEY = "priceshow_lang";

/** Idiomas suportados — referência canônica para o LanguageSwitcher. */
export const SUPPORTED_LANGUAGES = [
  { code: "pt-BR", label: "Português (BR)" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]["code"];

/** Lê o idioma do localStorage; devolve pt-BR se inválido ou ausente. */
function detectLanguage(): SupportedLanguage {
  try {
    const stored = localStorage.getItem(I18N_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) {
      return stored as SupportedLanguage;
    }
  } catch {
    // localStorage pode estar bloqueado em alguns contextos
  }
  return "pt-BR";
}

void i18n.use(initReactI18next).init({
  resources: {
    "pt-BR": { translation: ptBR },
    en: { translation: en },
    es: { translation: es },
  },
  lng: detectLanguage(),
  fallbackLng: "pt-BR",
  interpolation: {
    // React já escapa por padrão — não escapar duas vezes
    escapeValue: false,
  },
  // Sem backend assíncrono: os bundles são importados estaticamente acima.
  // Isso garante que o i18n está pronto de forma síncrona antes da primeira render.
});

export default i18n;
